import { type FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'

import { itemsLockedAtom } from '@/atoms/editorAtoms'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { Icon } from '@/components/priority/Icon/Icon'
import {
  HideInPdfBadge,
  ItemWrapper,
} from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import {
  ClassicControlToolbar,
  ClassicSelectionBorder,
  ClassicWarningBar,
} from '@/components/page/Items/ItemControlOverlay/classic/ClassicItemControlOverlay.styled'
import {
  ClassicCommonControls,
  ClassicDeleteControls,
  ClassicResizeControls,
} from '@/components/page/Items/ItemControlOverlay/classic/ClassicControls'
import { ClassicEdgeResizeControls } from '@/components/page/Items/ItemControlOverlay/classic/ClassicEdgeResizeControls'
import { EditorTextBoxConvertControls } from '@/components/page/Items/ItemControlOverlay/classic/EditorTextBoxConvertControls'
import { DragControls } from '@/components/page/Items/ItemControlOverlay/controls/CommonControls'
import { ImageControls } from '@/components/page/Items/ItemControlOverlay/controls/ImageControls'
import { TextControls } from '@/components/page/Items/ItemControlOverlay/controls/TextControls'
import { WarningIndicators } from '@/components/page/Items/ItemControlOverlay/controls/WarningIndicators'
import type { ItemControlState } from '@/types/itemControls'

type ClassicSelectedItemControlsProps = {
  itemId: string
  controlState: ItemControlState
  zoom: number
}

/**
 * Renders all controls for a single selected classic (TinyMCE-era) item:
 * text, image, line, QR code, watermark. The chrome is identical to
 * staging's; an editor text box or rule is drawn by
 * `SelectedItemControls.tsx`.
 *
 * Layout:
 * - Toolbar: horizontal bar centered above the item with action icons
 *   Order: Lock/Unlock | ConvertToText | ConvertToNewTextBox | Drag | Swap | QuickActions | Divider | Delete
 * - Warning bar: vertical stack on the right side of the item
 * - Resize handle: bottom-right corner
 *
 * The lock is the page's lock setting (`itemsLockedAtom`).
 */
export const ClassicSelectedItemControls: FC<
  ClassicSelectedItemControlsProps
> = ({ itemId, controlState, zoom }) => {
  const { t } = useTranslation()
  const itemsLocked = useRecoilValue(itemsLockedAtom)
  const { type, position, selection, image, text, warnings } = controlState
  const { x = 0, y = 0, width = 0, height = 0, rotate = 0 } = position || {}
  const {
    isEditing = false,
    disabled = false,
    assetLocked = false,
  } = selection || {}

  const isImageItem = type === 'image'
  const isTextItem = type === 'text'
  const isLineItem = type === 'line'

  // Hide most controls during editing mode
  const showControls = !isEditing && !disabled && !assetLocked

  // Resize handle should remain visible during editing (only hide when disabled/locked)
  const showResizeHandle = !disabled && !assetLocked

  // Check if we have any warnings to show
  const hasWarnings =
    (isImageItem && image?.showPrintQualityWarning) ||
    warnings?.showBleedWarning

  return (
    <ItemWrapper
      $x={x}
      $y={y}
      $width={width}
      $height={height}
      $rotate={rotate}
      data-item-id={itemId}
      data-item-control-overlay
    >
      <ClassicSelectionBorder
        data-selection-border
        $convertedToEditable={text?.convertedToEditable}
      />

      {/* Hide in PDF badge */}
      {controlState.hideInPdf && (
        <HideInPdfBadge $zoom={zoom}>
          <Tooltip
            text={t('editor.menus.properties.elements.hideInPdfLabel')}
            placement="right"
            offsetX={10}
            width="120px"
          >
            <Icon icon="eyeSlash" width="16px" height="16px" />
          </Tooltip>
        </HideInPdfBadge>
      )}

      {/* Toolbar above item with all action icons */}
      {showControls && (
        <ClassicControlToolbar $zoom={zoom} data-control-toolbar>
          <ClassicCommonControls itemsLocked={itemsLocked} />
          {isTextItem && <TextControls itemId={itemId} textState={text} />}
          {isTextItem && (
            <EditorTextBoxConvertControls
              itemId={itemId}
              itemsLocked={itemsLocked}
            />
          )}
          <DragControls itemId={itemId} itemsLocked={itemsLocked} />
          {isImageItem && (
            <ImageControls
              itemId={itemId}
              itemsLocked={itemsLocked}
              imageState={image}
            />
          )}
          <ClassicDeleteControls itemId={itemId} itemsLocked={itemsLocked} />
        </ClassicControlToolbar>
      )}

      {/* Warning indicators on right side */}
      {hasWarnings && (
        <ClassicWarningBar $zoom={zoom} data-warning-bar>
          <WarningIndicators
            itemId={itemId}
            itemType={type}
            imageState={image}
            warnings={warnings}
          />
        </ClassicWarningBar>
      )}

      {/* Resize handle at bottom-right corner */}
      {showResizeHandle && (
        <ClassicResizeControls
          itemId={itemId}
          itemsLocked={itemsLocked}
          isLineItem={isLineItem}
          zoom={zoom}
        />
      )}

      {/* Edge resize handles for images only (right + bottom edges) */}
      {showResizeHandle && isImageItem && (
        <ClassicEdgeResizeControls
          itemId={itemId}
          itemsLocked={itemsLocked}
          zoom={zoom}
          currentWidth={width}
          currentHeight={height}
        />
      )}
    </ItemWrapper>
  )
}
